/**
 * repriceOpenQuoteLines: IDI-364 / IDI-430 link 4.
 *
 * RULED (Lindsey, 2026-09-15; Adrian, 2026-09-15 + 09-29):
 *   - An OPEN quote (not sent, not pending/acknowledged signature, not
 *     converted to a job) picks up Item Catalog changes to a line's product:
 *     its COST (per bag / set / unit) and its COVERAGE chart.
 *   - The line keeps what the salesperson chose: its product, sq ft, its
 *     chosen coverage (R-value / thickness) and its OWN margin override.
 *     Only the material cost (and, for a chart product, sq ft per unit and the
 *     bag count) move.
 *   - A SENT, SIGNED or CONVERTED quote never re-prices. Those stay locked to
 *     the price the customer saw.
 *
 * Before this a line's cost was copied from the catalog when the line was
 * added and never read again (seedCatalog buildLineItem), so a $0 line stayed
 * $0 after the dealer priced the item, and delete + re-add was the only fix.
 *
 * Pure: returns { lines, changed } and never mutates. `changed` lists what
 * moved, for the "N lines updated from the Item Catalog" notice.
 */
import { synthesizeTenantProduct, isPerSqftCategory } from './seedCatalog';
import { coverageOptions, surfaceForSubArea, unitsFor, isOfferableOnSurface } from './productCoverage';

/** True when a quote may still re-price (open and unsent). */
export function isQuoteRepriceable(quote) {
  if (!quote) return false;
  const sig = quote.signatureStatus || quote.signature_status;
  if (sig === 'acknowledged' || sig === 'acknowledged_pending' || sig === 'declined') return false;
  const status = String(quote.status || '').toLowerCase();
  if (['sent', 'won', 'lost', 'expired', 'archived', 'converted'].includes(status)) return false;
  if (quote.convertedJobId || quote.converted_job_id || quote.signatureSentAt || quote.signature_sent_at) return false;
  return true;
}

// Material cost the catalog would charge for this line TODAY, or null when
// the line can't be re-priced from the catalog (custom line, product gone,
// product no longer priceable, a chosen coverage the chart no longer has).
function catalogMaterial(line, catalogBySku) {
  if (!line || line.isCustom || line.custom) return null;
  const row = catalogBySku.get(String(line.productId));
  if (!row) return null;
  const product = synthesizeTenantProduct(row);
  const costPerUnit = Number(product?.materialCostPerUnit) || 0;
  if (costPerUnit <= 0) return null;
  const sqft = Number(line.sqft) || 0;
  if (sqft <= 0) return null;
  // IDI-499 D2: AeroBarrier / Removal price sq ft x rate, never by coverage.
  if (isPerSqftCategory(product.categoryId)) return { materialCost: Math.round(sqft * costPerUnit) };

  const surface = line.coverageSurface || surfaceForSubArea(line.areaId, line.subAreaLabel);
  // IDI-434: never re-price a line from the other surface's chart; leave it.
  if (!isOfferableOnSurface(product, surface)) return null;
  const opts = coverageOptions(product, surface);
  if (opts.length) {
    // Keep the line's CHOSEN coverage; a one-entry chart needs no choice.
    const opt = opts.find((o) => o.value === line.coverageChoice) || (opts.length === 1 ? opts[0] : null);
    if (!opt) return null;
    return {
      materialCost: Math.round((sqft / opt.sqftPerUnit) * costPerUnit),
      coverageSqftPerUnit: opt.sqftPerUnit,
      bidItemUsage: unitsFor(sqft, opt.sqftPerUnit, opt.unitLabel),
      coverageUnit: opt.unitLabel,
    };
  }
  const cov = Number(product.squareFeetPerUnit) || 0;
  if (cov <= 1) return null;
  return { materialCost: Math.round((sqft / cov) * costPerUnit) };
}

/**
 * @param {object}   quote    the loaded quote record (status / signature fields)
 * @param {object[]} lines    the builder's normalized line items
 * @param {object[]} catalog  the tenant's offerable pricebook rows
 *                            (filterAreaPriceableRows output: dealer's own -> IDI central)
 */
export default function repriceOpenQuoteLines(quote, lines, catalog) {
  const safe = Array.isArray(lines) ? lines : [];
  if (!isQuoteRepriceable(quote) || !Array.isArray(catalog) || catalog.length === 0) {
    return { lines: safe, changed: [] };
  }
  const bySku = new Map(catalog.filter((r) => r && r.sku != null).map((r) => [String(r.sku), r]));
  const changed = [];
  const next = safe.map((line) => {
    const fresh = catalogMaterial(line, bySku);
    if (!fresh) return line;
    const before = Number(line.materialCost) || 0;
    const moved = fresh.materialCost !== before
      || (fresh.coverageSqftPerUnit != null && fresh.coverageSqftPerUnit !== Number(line.coverageSqftPerUnit))
      || (fresh.bidItemUsage != null && fresh.bidItemUsage !== Number(line.bidItemUsage));
    if (!moved) return line;
    changed.push({ id: line.id, label: line.productLabel || line.productId, from: before, to: fresh.materialCost });
    // lineMargin (the override) is untouched; the engine re-derives the price
    // from cost + that margin, or the cascade when there is no override.
    // Cached totals are dropped so they can't read as a price override.
    const { line_total: _lt, lineTotal: _lt2, cost_basis: _cb, ...rest } = line;
    return {
      ...rest,
      materialCost: fresh.materialCost,
      ...(fresh.coverageSqftPerUnit != null ? { coverageSqftPerUnit: fresh.coverageSqftPerUnit } : {}),
      ...(fresh.bidItemUsage != null ? { bidItemUsage: fresh.bidItemUsage } : {}),
      ...(fresh.coverageUnit ? { coverageUnit: fresh.coverageUnit } : {}),
    };
  });
  return { lines: next, changed };
}
